import { Button, DatePicker } from 'antd'
import dayjs from 'dayjs'
import { getAllRepairsForComponent } from 'features/assetModeling'
import {
  INPUT_STYLE,
  LabeledComponent,
  LabeledNumberInput,
  ParamUpdateTuple,
  useLabeledNumberInputParamUpdateProps,
} from 'features/fde/ProjectionParamEditorHelpers'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import at from 'lodash/at'
import isNil from 'lodash/isNil'
import set from 'lodash/set'
import some from 'lodash/some'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelJSON,
  GeckoComponentModelType,
  GeckoFeatureModelType,
  GeckoLegacyBoilerRepairRegion,
  GeckoLegacyBoilerRepairRegionJSON,
  GeckoMeasurementUnit,
  GeckoPlate,
  GeckoPlateJSON,
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
  regenerateAssetModel,
} from 'types'

const Container = styled.div`
  height: 100%;
`

const ActionContainer = styled.div`
  display: flex;
  height: 5%;
  justify-content: space-between;
  padding-top: 1rem;
`

const ParamContainer = styled.div`
  padding-top: 0.5rem;
`

const ParamFlexContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  padding-top: 0.5rem;
`

type RepairRegionParameter = ParamUpdateTuple<
  number | null,
  'nominalThickness'
> &
  ParamUpdateTuple<string | null, 'manufacturedDate'> &
  ParamUpdateTuple<number | null, 'startX'> &
  ParamUpdateTuple<number | null, 'startY'> &
  ParamUpdateTuple<number | null, 'endX'> &
  ParamUpdateTuple<number | null, 'endY'>

/**
 * A type that recursively sets all fields of an object to be optional
 */
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

type PartialRamRepairWithId = {
  id: string
  json:
    | DeepPartial<GeckoLegacyBoilerRepairRegionJSON>
    | DeepPartial<GeckoPlateJSON>
    | DeepPartial<GeckoPolygonalPlateJSON>
}

/**
 * Checks if all the necessary fields for config region has been inputted
 */
function isRepairValid(repair: PartialRamRepairWithId | null): boolean {
  if (isNil(repair)) {
    return false
  }
  if (!repair.id) {
    return false
  }

  const json = repair.json

  if (isNil(json.feature_geometry)) {
    return false
  }
  if (
    some(
      Object.values(json.feature_geometry),
      (value) => value === null || value === undefined
    )
  ) {
    return false
  }
  if (isNil(json.surface_specs)) {
    return false
  }

  return true
}

function getRamFieldSetter<T>(
  path: string,
  setLocalIndicationRepair: Dispatch<
    SetStateAction<PartialRamRepairWithId | null>
  >,
  indicationId: string,
  indicationType:
    | GeckoFeatureModelType.Plate
    | GeckoFeatureModelType.LegacyBoilerRepairRegion
    | GeckoFeatureModelType.PolygonalPlate
): (value: T | null) => void {
  return (value) => {
    setLocalIndicationRepair((prev) => {
      const newObj = {
        ...(prev ?? {
          id: indicationId,
          json: {
            name: 'Indication Repair',
            type: indicationType,
            surface_specs: {},
            feature_geometry: {},
          } as
            | DeepPartial<GeckoLegacyBoilerRepairRegionJSON>
            | DeepPartial<GeckoPlateJSON>
            | DeepPartial<GeckoPolygonalPlateJSON>,
        }),
      }
      if (!newObj.id) {
        newObj.id = indicationId
      }
      return set(
        newObj,
        `json.${path}`,
        value === null || value === undefined ? undefined : value
      )
    })
  }
}

function didRamFieldChange(
  path: string,
  localIndicationRepair: PartialRamRepairWithId | null,
  rolodexIndicationRepair:
    | GeckoLegacyBoilerRepairRegion
    | GeckoPlate
    | GeckoPolygonalPlate
    | null
): boolean {
  return (
    at(localIndicationRepair, `json.${path}`)[0] !==
    at(
      (rolodexIndicationRepair?.asJSON() ?? {}) as unknown as Record<
        string,
        unknown
      >,
      path
    )[0]
  )
}

function useRepairParam(
  localIndRepair: PartialRamRepairWithId | null,
  setLocalIndicationRepair: Dispatch<
    SetStateAction<PartialRamRepairWithId | null>
  >,
  rolodexIndicationRepair:
    | GeckoLegacyBoilerRepairRegion
    | GeckoPlate
    | GeckoPolygonalPlate
    | null,
  indicationId: string | undefined
): RepairRegionParameter | undefined {
  return useMemo(() => {
    if (
      rolodexIndicationRepair?.type === GeckoFeatureModelType.PolygonalPlate
    ) {
      return undefined
    }

    const xGeometryPrefix =
      rolodexIndicationRepair?.type === GeckoFeatureModelType.Plate
        ? 'x'
        : 'tube'
    if (!indicationId) {
      return undefined
    }
    return {
      nominalThickness:
        localIndRepair?.json.surface_specs?.wall_nominal_thickness ?? null,
      setNominalThickness: getRamFieldSetter(
        'surface_specs.wall_nominal_thickness',
        setLocalIndicationRepair,
        indicationId,
        rolodexIndicationRepair?.type ??
          GeckoFeatureModelType.LegacyBoilerRepairRegion
      ),
      nominalThicknessChanged: didRamFieldChange(
        'surface_specs.wall_nominal_thickness',
        localIndRepair,
        rolodexIndicationRepair
      ),
      manufacturedDate: localIndRepair?.json.installation_date ?? null,
      setManufacturedDate: getRamFieldSetter(
        'installation_date',
        setLocalIndicationRepair,
        indicationId,
        rolodexIndicationRepair?.type ??
          GeckoFeatureModelType.LegacyBoilerRepairRegion
      ),
      manufacturedDateChanged: didRamFieldChange(
        'installation_date',
        localIndRepair,
        rolodexIndicationRepair
      ),
      startX:
        // @ts-expect-error we assume the local indication repair is the same type as the rolodex one
        localIndRepair?.json.feature_geometry?.[`${xGeometryPrefix}_start`] ??
        null,

      setStartX: getRamFieldSetter(
        `feature_geometry.${xGeometryPrefix}_start`,
        setLocalIndicationRepair,
        indicationId,
        rolodexIndicationRepair?.type ??
          GeckoFeatureModelType.LegacyBoilerRepairRegion
      ),
      startXChanged: didRamFieldChange(
        `feature_geometry.${xGeometryPrefix}_start`,
        localIndRepair,
        rolodexIndicationRepair
      ),
      endX:
        // @ts-expect-error we assume the local indication repair is the same type as the rolodex one
        localIndRepair?.json.feature_geometry?.[`${xGeometryPrefix}_end`] ??
        null,
      setEndX: getRamFieldSetter(
        `feature_geometry.${xGeometryPrefix}_end`,
        setLocalIndicationRepair,
        indicationId,
        rolodexIndicationRepair?.type ??
          GeckoFeatureModelType.LegacyBoilerRepairRegion
      ),
      endXChanged: didRamFieldChange(
        `feature_geometry.${xGeometryPrefix}_end`,
        localIndRepair,
        rolodexIndicationRepair
      ),

      // @ts-expect-error we assume the local indication repair is the same type as the rolodex one
      startY: localIndRepair?.json.feature_geometry?.[`y_start`] ?? null,
      setStartY: getRamFieldSetter(
        `feature_geometry.y_start`,
        setLocalIndicationRepair,
        indicationId,
        rolodexIndicationRepair?.type ??
          GeckoFeatureModelType.LegacyBoilerRepairRegion
      ),
      startYChanged: didRamFieldChange(
        `feature_geometry.y_start`,
        localIndRepair,
        rolodexIndicationRepair
      ),
      // @ts-expect-error we assume the local indication repair is the same type as the rolodex one
      endY: localIndRepair?.json.feature_geometry?.[`y_end`] ?? null,
      setEndY: getRamFieldSetter(
        `feature_geometry.y_end`,
        setLocalIndicationRepair,
        indicationId,
        rolodexIndicationRepair?.type ??
          GeckoFeatureModelType.LegacyBoilerRepairRegion
      ),
      endYChanged: didRamFieldChange(
        `feature_geometry.y_end`,
        localIndRepair,
        rolodexIndicationRepair
      ),
    }
  }, [
    indicationId,
    localIndRepair,
    rolodexIndicationRepair,
    setLocalIndicationRepair,
  ])
}

export const Title = () => 'Repair Region'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps> = () => {
  const {
    indication: indicationItem,
    component: selectedComponent,
    inspection: selectedInspection,
    assetModel,
    forceRefresh,
  } = useIndicationData()

  const allRepairsForComponent =
    assetModel && selectedComponent
      ? getAllRepairsForComponent(assetModel, selectedComponent.id)
      : []

  const rolodexIndicationRepair =
    allRepairsForComponent.find((repair) => repair.id === indicationItem?.id) ??
    null

  const [localIndicationRepairJson, setLocalIndicationRepairJson] =
    useState<PartialRamRepairWithId | null>(() =>
      rolodexIndicationRepair
        ? {
            id: rolodexIndicationRepair.id,
            json: rolodexIndicationRepair.asJSON(),
          }
        : null
    )

  // - - - - - - - - - - - - - - - - - - - - - - - Save/Delete Logic - - - - - - - - - - - - - - - - - - - - -
  const [isChangingRam, setIsChangingRam] = useState(false)
  const { rolodexPut } = useRolodexEdit()
  const saveOrDeleteRamRepair = useCallback(
    async (isSaving: boolean) => {
      if (
        isNil(assetModel) ||
        isNil(selectedComponent) ||
        isNil(selectedInspection) ||
        isNil(localIndicationRepairJson)
      ) {
        return
      }

      try {
        setIsChangingRam(true)

        // a copy of the asset model that will be modified in-place
        const assetModelRawEditable = structuredClone(assetModel.raw) // structured clone to avoid modifying the original when modifying the child objcts
        const componentModelJson = (
          assetModelRawEditable.data.components as Record<
            string,
            GeckoComponentModelJSON
          >
        )[selectedComponent.id]
        if (!componentModelJson) {
          throw new Error('Component not found on asset model')
        }
        if (
          componentModelJson.type !== GeckoComponentModelType.LegacyBoilerWalls
        ) {
          throw new Error('Editing repairs is only set up for boilers')
        }
        if (
          localIndicationRepairJson.json.type !==
          GeckoFeatureModelType.LegacyBoilerRepairRegion
        ) {
          throw new Error(
            'Editing repairs is only set up for boiler repair regions'
          )
        }

        const allOtherFeatureEntries = Object.entries(
          componentModelJson.features
        ).filter(([id]) => id !== localIndicationRepairJson.id)

        const newFeatureEntries = !isSaving
          ? allOtherFeatureEntries
          : [
              ...allOtherFeatureEntries,
              [localIndicationRepairJson.id, localIndicationRepairJson.json],
            ]
        componentModelJson.features = Object.fromEntries(newFeatureEntries)

        const newAssetModelPreRegen = GeckoAssetModel.CreateFromJSON(
          assetModelRawEditable
        )
        const assetModelPatchJson = regenerateAssetModel(newAssetModelPreRegen)
        await rolodexPut([assetModelPatchJson])
        await forceRefresh()
      } catch (error) {
        console.error('Error saving or deleting repair:', error)
      } finally {
        setIsChangingRam(false)
      }
    },
    [
      assetModel,
      forceRefresh,
      localIndicationRepairJson,
      rolodexPut,
      selectedComponent,
      selectedInspection,
    ]
  )

  // - - - - - - - - - - - - - - - - - - - - - - - Check Logic - - - - - - - - - - - - - - - - - - - - -
  const isConfigValid = isRepairValid(localIndicationRepairJson)

  // - - - - - - - - - - - - - - - - - - - - - PARAMETERS - - - - - - - - - - - - - - - - - - - - -
  const widthMeasurementUnit: GeckoMeasurementUnit =
    assetModel && assetModel.data.type === GeckoAssetModelType.LegacyBoiler
      ? GeckoMeasurementUnit.Tubes
      : GeckoMeasurementUnit.Feet

  const params = useRepairParam(
    localIndicationRepairJson,
    setLocalIndicationRepairJson,
    rolodexIndicationRepair,
    indicationItem?.id
  )

  const nominalThicknessProps = useLabeledNumberInputParamUpdateProps(
    'Nominal Thickness',
    'nominalThickness',
    params,
    GeckoMeasurementUnit.Meters
  )
  const startXProps = useLabeledNumberInputParamUpdateProps(
    'Start X',
    'startX',
    params,
    widthMeasurementUnit
  )
  const startYProps = useLabeledNumberInputParamUpdateProps(
    'Start Y',
    'startY',
    params,
    GeckoMeasurementUnit.Meters
  )
  const endXProps = useLabeledNumberInputParamUpdateProps(
    'End X',
    'endX',
    params,
    widthMeasurementUnit
  )
  const endYProps = useLabeledNumberInputParamUpdateProps(
    'End Y',
    'endY',
    params,
    GeckoMeasurementUnit.Meters
  )

  if (params === null) {
    return (
      <Container>
        Cannot edit polygonal plate parameters directly here.
      </Container>
    )
  }

  return (
    <Container>
      <ParamContainer>
        <LabeledComponent label={'Repair Date'}>
          <DatePicker
            value={
              params?.manufacturedDate ? dayjs(params.manufacturedDate) : null
            }
            onChange={(value) => {
              if (!params) {
                return
              }
              if (!value) {
                params.setManufacturedDate(null)
                return
              }
              params.setManufacturedDate(value.format('YYYY-MM-DD'))
            }}
            format={'MMMM D, YYYY'}
            status={params?.manufacturedDateChanged ? 'warning' : undefined}
            {...INPUT_STYLE}
          />
        </LabeledComponent>
      </ParamContainer>
      <ParamContainer>
        <LabeledNumberInput {...nominalThicknessProps} />
      </ParamContainer>
      <ParamFlexContainer>
        <LabeledNumberInput {...startXProps} />
        <LabeledNumberInput {...endXProps} />
      </ParamFlexContainer>
      <ParamFlexContainer>
        <LabeledNumberInput {...startYProps} />
        <LabeledNumberInput {...endYProps} />
      </ParamFlexContainer>
      <ActionContainer>
        <Button
          style={{ width: '49%' }}
          onClick={() =>
            setLocalIndicationRepairJson(
              rolodexIndicationRepair
                ? {
                    id: rolodexIndicationRepair.id,
                    json: rolodexIndicationRepair.asJSON(),
                  }
                : null
            )
          }>
          Discard Changes
        </Button>
        <Button
          style={{ width: '49%' }}
          disabled={isChangingRam || !isConfigValid}
          onClick={() => {
            saveOrDeleteRamRepair(false).then(() => {
              setLocalIndicationRepairJson(null)
            })
          }}>
          Delete Repair
        </Button>
      </ActionContainer>
      <ActionContainer>
        <Button
          style={{ width: '100%' }}
          disabled={isChangingRam || !isConfigValid}
          onClick={() => saveOrDeleteRamRepair(true)}>
          Save Changes
        </Button>
      </ActionContainer>
    </Container>
  )
}
